import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * "Forgot your password?" — the two routes (owner request 2026-10-12), built here so `router.tsx` gains one line each,
 * as the emailed-link pages do (`../mail-landing/mail-landing-route.tsx`).
 *
 * Children of the ROOT, never of the shell: the person is signed out by definition, and the shell's guard would send
 * them to log in. The reset link's token is its FRAGMENT (the API's `tokenLinkOf`), read once and then taken out of
 * the address bar, so it lingers in no history entry, screenshot or shared address; nothing is posted until the person
 * submits the new password.
 */
import { createRoute, useLocation, useNavigate, type AnyRoute } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";

import { ForgotPasswordScreen, ResetPasswordScreen } from "./password-reset-screen.js";

export const FORGOT_PASSWORD_PATH = "/forgot-password";
/** The API's `MAIL_LINK_PATH.passwordReset` (apps/api/src/mail/link.ts), restated: apps never import apps. */
export const RESET_PASSWORD_PATH = "/reset-password";

/** The fragment's token, kept in state; the address bar is replaced by the bare path. */
function useFragmentToken(): string {
  const hash = useLocation({ select: (location) => location.hash.replace(/^#/, "") });
  const [token] = useState(hash);
  const navigate = useNavigate();
  useEffect(() => {
    if (hash !== "") void navigate({ to: RESET_PASSWORD_PATH, hash: "", replace: true });
  }, [hash, navigate]);
  return token;
}

function ForgotPasswordRoute(): ReactNode {
  usePortalTranslation("core");
  return <ForgotPasswordScreen />;
}

function ResetPasswordRoute(): ReactNode {
  usePortalTranslation("core");
  return <ResetPasswordScreen token={useFragmentToken()} />;
}

export function forgotPasswordRouteUnder<TParent extends AnyRoute>(parent: TParent) {
  return createRoute({
    getParentRoute: () => parent,
    path: FORGOT_PASSWORD_PATH,
    staticData: { pageTitle: () => portalText("core", "password_reset.forgot_title") },
    component: ForgotPasswordRoute,
  });
}

export function resetPasswordRouteUnder<TParent extends AnyRoute>(parent: TParent) {
  return createRoute({
    getParentRoute: () => parent,
    path: RESET_PASSWORD_PATH,
    staticData: { pageTitle: () => portalText("core", "password_reset.reset_title") },
    component: ResetPasswordRoute,
  });
}
